import { DataTablePrimeReact } from "@finanedi.es/storybook"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { SumaSaldoInformeDtoProps } from "api/DTOs/SumaSaldoInformeDto"
import {
  SumaSaldoByFiltersCall,
  generateSumaSaldoExcel,
  getSumaSaldoByFilters,
} from "api/SumaSaldo.service"
import { useDownloadFileContext } from "common/context/DownloadFileProvider"
import SubMenuHeader from "features/Contabilidad/components/SubmenuHeader"
import {
  InformesCommonProps,
  formatInformesStorage,
} from "features/Contabilidad/contants"
import { SumasYSaldosTable } from "features/Contabilidad/pages/SumasSaldos/SumasYSaldosTable"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { backendDateFormat } from "utils/formatDate"
import {
  CUENTA_CONTABLE_GRUPO_OPTIONS,
  TIPO_INFORMES_SUMA_SALDO,
} from "./constants"

const SumasSaldos = () => {
  const { t } = useTranslation()
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })

  const dtRef = useRef<DataTablePrimeReact<SumaSaldoInformeDtoProps[]> | null>(
    null
  )
  const { handleDirectDownloadExcel } = useDownloadFileContext()

  const [informesFilters, setInformesFilters] = useState<InformesCommonProps>(
    formatInformesStorage()
  )

  const { data: informe, isFetching: informeLoading } = useQuery({
    queryKey: [
      "getBalance",
      informesFilters?.dateFilter?.[0],
      informesFilters?.dateFilter?.[1],
      informesFilters.searchValue,
      informesFilters?.sumasSaldos?.firstDropdownValue,
      informesFilters?.sumasSaldos?.secondDropdownValue,
      informesFilters?.SoloCuentasConSaldo,
      pagination.itemsPerPage,
      pagination.skipCount,
    ],
    queryFn: () => {
      SumaSaldoByFiltersCall?.cancel()
      return getSumaSaldoByFilters({
        CuentaContableGrupo:
          informesFilters?.sumasSaldos?.firstDropdownValue ?? undefined,
        TipoInformeSumaSaldo:
          informesFilters?.sumasSaldos?.secondDropdownValue ?? 0,
        MaxResultCount: pagination.itemsPerPage,
        SkipCount: pagination.skipCount,
        SoloCuentasConSaldo: informesFilters?.SoloCuentasConSaldo,
        FechaInicioMovimiento: informesFilters?.dateFilter?.[0]
          ? backendDateFormat(new Date(informesFilters?.dateFilter?.[0]))
          : undefined,
        FechaFinMovimiento: informesFilters?.dateFilter?.[1]
          ? backendDateFormat(new Date(informesFilters?.dateFilter?.[1]))
          : undefined,
        Keyword:
          informesFilters.searchValue !== ""
            ? informesFilters.searchValue
            : undefined,
      })
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const [isLoadingExcel, setIsLoadingExcel] = useState(false)
  const handleDownLoadExcel = async () => {
    setIsLoadingExcel(true)
    const archivo = await generateSumaSaldoExcel({
      CuentaContableGrupo:
        informesFilters?.sumasSaldos?.firstDropdownValue ?? undefined,
      TipoInformeSumaSaldo:
        informesFilters?.sumasSaldos?.secondDropdownValue ?? 0,
      MaxResultCount: pagination.itemsPerPage,
      SkipCount: pagination.skipCount,
      SoloCuentasConSaldo: informesFilters?.SoloCuentasConSaldo,
      FechaInicioMovimiento: informesFilters?.dateFilter?.[0]
        ? backendDateFormat(new Date(informesFilters?.dateFilter?.[0]))
        : undefined,
      FechaFinMovimiento: informesFilters?.dateFilter?.[1]
        ? backendDateFormat(new Date(informesFilters?.dateFilter?.[1]))
        : undefined,
      Keyword:
        informesFilters.searchValue !== ""
          ? informesFilters.searchValue
          : undefined,
    })
    if (archivo) {
      handleDirectDownloadExcel(archivo, "SumaSaldos.xls")
    }
    setIsLoadingExcel(false)
  }

  useEffect(() => {
    sessionStorage.setItem(
      "informesFilters",
      JSON.stringify({
        ...informesFilters,
        dateFilter:
          informesFilters?.dateFilter === undefined
            ? undefined
            : [
                informesFilters?.dateFilter?.[0],
                informesFilters?.dateFilter?.[1] ?? null,
              ],
        searchValue: informesFilters?.searchValue,
      })
    )
  }, [informesFilters, pagination.itemsPerPage, pagination.skipCount])

  return (
    <section className='tw-h-[calc(100vh-200px)]'>
      <SubMenuHeader
        title={`${t("informes.selectedTab.headerAdditionsBalances")}`}
        informesFilters={informesFilters}
        setInformesFilters={setInformesFilters}
        handleDownload={() => handleDownLoadExcel()}
        secondOptions={TIPO_INFORMES_SUMA_SALDO(t)}
        secondPlaceholder={`${t("informes.saldos.filter.subAccountLabel")}`}
        firstOptions={CUENTA_CONTABLE_GRUPO_OPTIONS(t)}
        firstPlaceholder={`${t(
          "informes.perdidasGanancias.header.allGroupLabel"
        )}`}
        isLoadingExcel={isLoadingExcel}
        mode='sumasSaldos'
      />
      <SumasYSaldosTable
        dtRef={dtRef}
        setPagination={setPagination}
        items={informe?.items}
        totalCount={informe?.totalCount}
        isLoading={informeLoading}
        pagination={pagination}
      />
    </section>
  )
}

export default SumasSaldos
